import { EdfTeams, GeniusSportMatches } from '@/lib/directus';

export type Button = {
  buttonLabel?: string;
  buttonStyle?: 'primary' | 'secondary' | 'transparent_with_ball';
};

export type ButtonBallColor = 'white' | 'red';

export type CampaignsInfo = {
  competitionNamesById: { [key: string]: string };
  competitionsGroupedByTeam: Record<string, Record<string, Array<number>>>;
  competitionPhotosById: { [key: string]: string };
  defaultFilter: { teamName: string; teamGender: Gender; competition: number; year: number };
  teamName: string;
  teamGender: Gender;
  teamsDetails: Array<EdfTeams>;
  genderByTeam: Record<string, Gender>;
};

export type CardInfo = {
  background?: PushCardBackground;
  backgroundEllipse?: string;
  eyebrow_text?: string;
  title: string;
  description?: string;
  button?: Button;
  callToActionUrl: Url;
  category?: string;
  isoDate?: string;
  dateLabelType?: DateLabelType;
  image?: string;
  imageAlt?: string;
  imageBlur?: string;
  imagePosition?: 'left' | 'right';
  imageSizes?: string;
  imageHeight?: string;
  imageDimensions?: { width?: number; height?: number };
  intro?: string;
  grayBackground?: boolean;
  socials_items?: Array<{ title: string; url: string; image: string }>;
  photoCount?: number;
  duration?: string;
};

export type CardVideoInfo = CardInfo & {
  button?: Array<{
    label: string;
    url: Url;
  }>;
  videoId: string;
  duration?: string;
};

export type CardFaqInfo = CardInfo & {
  questions?: Array<{
    question: string;
    answer: string;
  }>;
  isOpen?: boolean;
  isAllOpen?: boolean;
};

export type CardCalendarInfo = CardInfo & {
  currentPeriod?: string;
  clock?: string;
  team1?: {
    name: string;
    logo?: string;
    score?: number;
  };
  team2?: {
    name: string;
    logo?: string;
    score?: number;
  };
  gsId?: string | GeniusSportMatches | null;
  preparation?: boolean | null;
  phase?: string | null;
  journee?: string | null;
  place?: string;
  quarterScores?: Array<[number, number]> | null;
  live?: boolean;
};

export type CardPlayerInfo = CardInfo & {
  playerId?: number;
  birthDate?: string;
  clubName?: string;
  fullName?: string;
  height?: string;
  man?: boolean;
  number?: number;
  position?: string;
  selected?: boolean;
};

export type BlockColumnHeight = 'default' | 'medium' | 'high';

export type DateLabelType =
  | 'default'
  | 'since'
  | 'full'
  | 'live'
  | 'time'
  | 'year'
  | 'dd/MM/yyyy'
  | 'MMM yyyy'
  | 'dd/MM/yyyy HH:mm'
  | 'd MMMM yyyy HH:mm'
  | 'd MMMM yyyy';
export type Display = 'simple' | 'full' | 'search';

export type FormInfo = {
  block_title: string;
  description: string;
  formId: string;
  fields: Array<{
    label: string;
    file_extension?: string;
    required: boolean;
    type: 'email' | 'file' | 'select' | 'text' | 'textarea' | 'hidden' | 'checkbox' | 'wysiwyg';
    name: string;
    selectValues: Array<{ key: string; value: string }>;
    sort: number;
    value?: string;
    wysiwyg?: string;
  }>;
  submit_label: string;
  title: string;
  submitSuccessMessage: string;
  submitErrorMessage: string;
  defaultHiddenValue: string;
};

export type GalleryVideoInfo = {
  title: string;
  callToActionUrl: Url;
  videos: Array<CardVideoInfo>;
};

export enum Gender {
  Male = 1,
  Female = 2,
}

export type HeroInfo = {
  title: string;
  description?: string;
  callToActionUrl: Url;
  category?: string;
  image?: string;
  imageBlur?: string;
  isoDate?: string;
};

export type ImageMetadata = {
  id: string;
  title: string;
};

export type PrizeInfo = {
  date: string;
  location: string;
  rank: string;
  rank_int: number;
};

export type PrizeByTeam = {
  [team: string]: PrizeInfosByTitle;
};

export type PrizeInfosByTitle = {
  prizeInfosByTitle: { [title: string]: Array<PrizeInfo> };
  competitionTitleOrders: Record<string, number>;
};

export type PushCardBackground = 'default' | 'blue' | 'gradient';

export type TeamMatch = {
  id: number;
  numero?: number;
  competition: string;
  date: string;
  country: string;
  opponent: string;
  place: string;
  result: string;
  matchId?: number;
  playerPoints?: number;
  maj_n_npj?: boolean | null;
  score_opponent: number;
  score_france: number;
  france_at_home?: boolean | null;
};

export type TeamMatchHistory = {
  competitions: Array<string>;
  countries: Array<string>;
  cities: Array<string>;
  matchs: Array<TeamMatch>;
  opponents: Array<string>;
  years: Array<number>;
};

export type PlayerMatchHistory = {
  matchs: Array<TeamMatch>;
  playerFullName?: string;
  playerId?: number;
};

export type PlayerCareer = {
  id: number;
  firstName: string;
  lastName: string;
  height?: number | null;
  playerPoints: number;
  maj_n_npj?: boolean | null;
  playerSelections: number;
};

export type RosterMatch = {
  height?: number;
  player: {
    id: number;
    first_name: string;
    last_name: string;
    height?: number;
    maj_n_npj?: boolean | null;
    club: string;
    points: number;
  };
};

export type RosterMatchHistory = {
  roster: Array<RosterMatch>;
  match: string;
  matchId?: number;
  matchDate: string;
  matchLocation: string;
  matchUrlArticle: string;
  matchUrlLive: string;
  matchUrlSheet: string;
  matchUrlGallery: string;
  matchUrlVideo: string;
  matchUrlTicketing: string;
};

export type TabInfo = {
  name: string;
  hash?: string;
  liveIndicator?: boolean;
};

export type Url = {
  url: string;
  external: boolean;
  slug?: string;
  linkText?: string;
  hasTab?: boolean;
};

export type Iframeinfo = {
  src: string;
  title: string;
  height: number;
  width: number;
};

export type WysiwygInfo = {
  title?: string;
  html: string;
  styles?: string;
};
